import { inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of, switchMap } from "rxjs";
import { catchError, map, withLatestFrom } from "rxjs/operators";

import { HttpResponse } from "@angular/common/http";
import {
  LookupApiResponse,
  RequestOtpApiResponse,
} from "@core/models/api.response";
import {
  LookupDTO,
  PepClubCheckDTO,
  RequestOtpViaMsisdnDTO,
  ValidateOtpDTO,
} from "@core/models/dto.models";
import { Email } from "@core/models/registration.interface";
import { EmailService } from "@core/services/email.service";
import { LookupService } from "@core/services/lookup.service";
import { MsisdnService } from "@core/services/msisdn.service";
import { OtpService } from "@core/services/otp.service";
import { PepClubService } from "@core/services/pepclub.service";
import { RegisterService } from "@core/services/register.service";
import { Store } from "@ngrx/store";
import { ErrorDialogComponent } from "@shared/components/error-dialog/error-dialog.component";
import { IntentTypes } from "@shared/enums";
import { loginActions } from "../../login/store/actions";
import { registrationActions } from "./actions";
import { selectLoginDetails, selectRegistrationState } from "./selectors";

//Lookup effects
export const registrationLookUpEffect = createEffect(
  (
    actions$ = inject(Actions),
    lookupService: LookupService = inject(LookupService)
  ) => {
    return actions$.pipe(
      ofType(registrationActions.requestLookup),
      switchMap((dto: LookupDTO) => {
        return lookupService.lookup(dto).pipe(
          map((data: LookupApiResponse) => {
            if (data.intent !== IntentTypes.empty) {
              if (data.intent === IntentTypes.login) {
                return registrationActions.requestLookupFailed({
                  error:
                    "This identification number has already been registered, please try logging in.",
                });
              }

              return registrationActions.requestLookupSuccess({
                ...data,
                intent:
                  data.intent === IntentTypes.register
                    ? IntentTypes.new //Change back IntentTypes.new
                    : data.intent,
                lookupData: dto,
              });
            } else {
              return registrationActions.requestLookupFailed({
                error: data.error,
              });
            }
          }),
          catchError((error) => {
            return of(
              registrationActions.requestLookupFailed({
                error: "Something went wrong. Please try again later.",
              })
            );
          })
        );
      })
    );
  },
  { functional: true }
);

export const registrationLookUpSuccessEffect = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(registrationActions.requestLookupSuccess),
      switchMap((data) => {
        if (data.intent === IntentTypes.partial) {
          return of(
            registrationActions.requestOTPViaMsisdn({
              guid: data.guid,
            } as RequestOtpViaMsisdnDTO)
          );
        } else {
          return of(data);
        }
      })
    );
  },
  { dispatch: false, functional: true }
);

export const registrationLookUpEffectFailed = createEffect(
  (actions$ = inject(Actions), dialog = inject(MatDialog)) => {
    return actions$.pipe(
      ofType(registrationActions.requestLookupFailed),
      map((data) => {
        dialog.open(ErrorDialogComponent, {
          data: {
            errorMessage: data.error,
          },
        });
      })
    );
  },

  { dispatch: false, functional: true }
);

//Check for Pepclub effects
export const registrationRequestPepclubCheckEffect = createEffect(
  (
    actions$ = inject(Actions),
    pepClubService: PepClubService = inject(PepClubService),
    store = inject(Store)
  ) => {
    return actions$.pipe(
      ofType(registrationActions.requestPepclubCheck),
      withLatestFrom(store.select(selectRegistrationState)), // Combine with store data
      switchMap(([action, registrationData]) => {
        return pepClubService
          .checkIfPepClubOK({
            msisdn: registrationData.data.mobile.number,
            guid: registrationData.data.guid,
          } as PepClubCheckDTO)
          .pipe(
            map((res) => {
              if (res.okForPepClub) {
                return registrationActions.requestPepclubCheckSuccess({
                  okForPepClub: true,
                  msisdn: registrationData.data.mobile.number,
                });
              } else {
                return registrationActions.requestPepclubCheckFailed({
                  okForPepClub: false,
                  message: "The provided number is invalid",
                });
              }
            }),
            catchError((error) =>
              of(
                registrationActions.requestPepclubCheckFailed({
                  okForPepClub: false,
                  message:
                    error.error.message ?? "The provided number is invalid",
                })
              )
            )
          );
      })
    );
  },
  { functional: true }
);

export const registrationRequestPepclubCheckSuccessEffect = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(registrationActions.requestPepclubCheckSuccess),
      switchMap((data) => {
        return of(
          registrationActions.requestOTPViaMsisdn({
            msisdn: data.msisdn,
          } as RequestOtpViaMsisdnDTO)
        );
      })
    );
  },
  { dispatch: true, functional: true }
);

export const registrationRequestPepclubCheckFailedEffect = createEffect(
  (actions$ = inject(Actions), dialog = inject(MatDialog)) => {
    return actions$.pipe(
      ofType(registrationActions.requestPepclubCheckFailed),
      map((data) => {
        dialog.open(ErrorDialogComponent, {
          data: {
            errorMessage: data.message,
          },
        });
      })
    );
  },

  { dispatch: false, functional: true }
);

//Check for Valid Msisdn effects
export const registrationRequestMsisdnCheckEffect = createEffect(
  (
    actions$ = inject(Actions),
    msisdnService: MsisdnService = inject(MsisdnService),
    store = inject(Store)
  ) => {
    return actions$.pipe(
      ofType(registrationActions.requestCheckMsisdn),
      switchMap((data) => {
        return msisdnService.validateMsisdn(data.msisdn).pipe(
          map((res) => {
            if (!res.exists) {
              return registrationActions.requestCheckMsisdnSuccess({
                exists: true,
                msisdn: data.msisdn,
              });
            } else {
              return registrationActions.requestCheckMsisdnFailed({
                exists: false,
                message: "The provided number is already in use",
              });
            }
          }),
          catchError((error) =>
            of(
              registrationActions.requestPepclubCheckFailed({
                okForPepClub: false,
                message:
                  error.error.message ?? "The provided number is invalid",
              })
            )
          )
        );
      })
    );
  },
  { functional: true }
);

export const registrationRequestMsisdnCheckSuccessEffect = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(registrationActions.requestCheckMsisdnSuccess),
      switchMap((data) => {
        return of(
          registrationActions.requestOTPViaMsisdn({
            msisdn: data.msisdn,
          } as RequestOtpViaMsisdnDTO)
        );
      })
    );
  },
  { dispatch: true, functional: true }
);

export const registrationRequestMsisdnCheckFailedEffect = createEffect(
  (actions$ = inject(Actions), dialog = inject(MatDialog)) => {
    return actions$.pipe(
      ofType(registrationActions.requestCheckMsisdnFailed),
      map((data) => {
        dialog.open(ErrorDialogComponent, {
          data: {
            errorMessage: data.message,
          },
        });
      })
    );
  },

  { dispatch: false, functional: true }
);

//Request OTP effects
export const registrationRequestOtpEffect = createEffect(
  (actions$ = inject(Actions), otpService: OtpService = inject(OtpService)) => {
    return actions$.pipe(
      ofType(registrationActions.requestOTPViaMsisdn),
      switchMap((dto: RequestOtpViaMsisdnDTO) => {
        return otpService.requestOtp(dto).pipe(
          map((response: HttpResponse<RequestOtpApiResponse>) => {
            if (response?.status === 204) {
              return registrationActions.requestOTPViaMsisdnSuccess({
                msisdn: dto.msisdn,
              });
            } else {
              return registrationActions.requestOTPViaMsisdnFailed({
                error: `Could not send OTP to number: ${dto.msisdn}`,
                msisdn: "",
              });
            }
          }),
          catchError((error) => {
            return of(
              registrationActions.requestOTPViaMsisdnFailed({
                error:
                  error.error.message ??
                  `Could not send OTP to number: ${dto.msisdn}`,
                msisdn: "",
              })
            );
          })
        );
      })
    );
  },
  { functional: true }
);

export const registrationRequestOtpFailedEffect = createEffect(
  (actions$ = inject(Actions), dialog = inject(MatDialog)) => {
    return actions$.pipe(
      ofType(registrationActions.requestOTPViaMsisdnFailed),
      map((data) => {
        dialog.open(ErrorDialogComponent, {
          data: {
            errorMessage: data.error,
          },
        });
      })
    );
  },

  { dispatch: false, functional: true }
);

//Validate OTP effects
export const registrationValidateOtpEffect = createEffect(
  (actions$ = inject(Actions), otpService: OtpService = inject(OtpService)) => {
    return actions$.pipe(
      ofType(registrationActions.requestOTPValidation),
      switchMap((dto: ValidateOtpDTO) => {
        return otpService.validateOtp(dto).pipe(
          map((response) => {
            if (response?.match) {
              return registrationActions.requestOTPValidationSuccess({
                msisdn: dto.msisdn,
                otp: dto.otp,
                match: true,
              });
            } else {
              return registrationActions.requestOTPValidationFailed({
                message: `OTP is Incorrect`,
                msisdn: dto.msisdn,
                otp: "",
              });
            }
          }),
          catchError((error) =>
            of(
              registrationActions.requestOTPValidationFailed({
                message: error.error.message ?? `OTP is Incorrect`,
                msisdn: dto.msisdn,
                otp: "",
              })
            )
          )
        );
      })
    );
  },
  { functional: true }
);

export const registrationValidatetOtpFailedEffect = createEffect(
  (actions$ = inject(Actions), dialog = inject(MatDialog)) => {
    return actions$.pipe(
      ofType(registrationActions.requestOTPValidationFailed),
      map((data) => {
        dialog.open(ErrorDialogComponent, {
          data: {
            errorMessage: data.message,
          },
        });
      })
    );
  },

  { dispatch: false, functional: true }
);

//Email effects

export const registrationValidateEmailEffect = createEffect(
  (
    actions$ = inject(Actions),
    emailService: EmailService = inject(EmailService)
  ) => {
    return actions$.pipe(
      ofType(registrationActions.requestEmailValidation),
      switchMap((dto: Email) => {
        return emailService.validateEmail(dto.email).pipe(
          map((response) => {
            if (!response?.exists) {
              return registrationActions.requestEmailValidationSuccess({
                exists: true,
              });
            } else {
              return registrationActions.requestEmailValidationFailed({
                message: `Email already exists`,
                exists: true,
              });
            }
          }),
          catchError((error) =>
            of(
              registrationActions.requestEmailValidationFailed({
                message: error.error.message ?? `Email already exists`,
              })
            )
          )
        );
      })
    );
  },
  { functional: true }
);

export const registrationValidatetEmailFailedEffect = createEffect(
  (actions$ = inject(Actions), dialog = inject(MatDialog)) => {
    return actions$.pipe(
      ofType(registrationActions.requestEmailValidationFailed),
      map((data) => {
        dialog.open(ErrorDialogComponent, {
          data: {
            errorMessage: data.message,
          },
        });
      })
    );
  },

  { dispatch: false, functional: true }
);

//Register effects
export const registrationRequestRegisterEffect = createEffect(
  (
    actions$ = inject(Actions),
    registerService: RegisterService = inject(RegisterService),
    store = inject(Store)
  ) => {
    return actions$.pipe(
      ofType(registrationActions.requestRegistration),
      withLatestFrom(store.select(selectRegistrationState)), // Combine with store data
      switchMap(([action, registrationData]) => {
        return registerService.register(registrationData).pipe(
          map(() => {
            return registrationActions.requestRegistrationSuccess();
          }),
          catchError((error) =>
            of(
              registrationActions.requestRegistrationFailed({
                message: error.error.message ?? "Could not register user",
              })
            )
          )
        );
      })
    );
  },
  { functional: true }
);

export const registrationRequestRegisterSuccessEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(registrationActions.requestRegistrationSuccess),
      withLatestFrom(store.select(selectLoginDetails)), // Combine with store data
      switchMap(([action, loginDetails]) =>
        of(
          loginActions.requestLogin({
            email: loginDetails.email,
            password: loginDetails.password,
          })
        )
      )
    );
  },
  { dispatch: true, functional: true }
);

export const registrationRequestRegisterFailedEffect = createEffect(
  (actions$ = inject(Actions), dialog = inject(MatDialog)) => {
    return actions$.pipe(
      ofType(registrationActions.requestRegistrationFailed),
      map((data) => {
        dialog.open(ErrorDialogComponent, {
          data: {
            errorMessage: data.message,
          },
        });
      })
    );
  },

  { dispatch: false, functional: true }
);
